import { createReducer } from "@reduxjs/toolkit";
import {
  dProductsCheckedAllItemAC,
  dProductsCheckedItemAC,
  dProductsClearDetailItemAC,
  dProductsClearFiltersAC,
  dProductsSetDataAC,
  dProductsSetDetailItemAC,
  dProductsSetFiltersAC,
  dProductsSetItemsForPageAC,
  dProductsSetPageNumberAC,
  dProductsSetPaginationAC,
} from "./dProductsActionCreators";
import { typePagination } from "model/system/constants";
import { PRODUCT_OWNER } from "../../constants/productOwner";
import {logoutAC, resetReducers} from "model/system/systemActionCreators";
import { IdStoresStateDataItem } from "model/dashboardStores/dStoresReducer";

// Type
export type IdProductsStateDataItem = {
  checked: boolean;
  id: string;
  name: string;
  barcodes: string[];
  psid: string | null;
  units: string;
  integerUnit: boolean;
  marked: boolean;
  active: boolean;
  deleted: boolean;
  category: {
    id: string;
    name: string;
    deleted: boolean;
  } | null;
  stores: {
    id: string;
    store: Omit<IdStoresStateDataItem, "checked" | "cash_balance">;
    active: boolean;
    price: number;
  }[];
  vat: number;
  commissionTIN?: string;
  currentBarcode?: string;
  commissionPINFL?: string;
  unitCode?: number;
  packageCode?: string;
  productOwnerType: PRODUCT_OWNER;
};

export type IdProductsState = {
  pagination: typePagination;
  data: IdProductsStateDataItem[];
  detailItem: IdProductsStateDataItem | undefined;
  filters: typeProductsFilters;
};

export type typeProductsFilters = {
  invalidPsid?: true | null;
  name?: string | null;
  category?: string | null;
  integerUnit?: boolean | null;
  active?: boolean | null;
  marked?: boolean | null;
  deleted?: boolean | null;
  store?: string | null;
  barcode?: string | null;
};

// Clean filter object
export const cleanFilterObject: typeProductsFilters = {
  invalidPsid: null,
  integerUnit: null,
  active: null,
  marked: null,
  deleted: false,
  category: undefined,
  store: null,
};

// Init state
export const initialState: IdProductsState = {
  pagination: {
    pageNumber: 0,
    itemsPerPage: 14,
  },
  data: [],
  detailItem: undefined,
  filters: cleanFilterObject,
};

// Reducer
export const DProductsReducer = createReducer(initialState, (builder) => {
  builder

    // Reducer of set data
    .addCase(dProductsSetDataAC, (state, action) => {
      state.data = action.payload.dataList;
    })

    // Reducer of set pagination
    .addCase(dProductsSetPaginationAC, (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload.pagination };
    })

    // Reducer of set items for page pagination
    .addCase(dProductsSetItemsForPageAC, (state, action) => {
      state.pagination = {
        ...state.pagination,
        pageNumber: 0,
        itemsPerPage: action.payload.itemsForPage,
      };
    })

    // Reducer of set pageNumber pagination
    .addCase(dProductsSetPageNumberAC, (state, action) => {
      state.pagination.pageNumber = action.payload.pageNumber;
    })

    // Reducer of set detail item
    .addCase(dProductsSetDetailItemAC, (state, action) => {
      state.detailItem = state.data.find((item) => item.id === action.payload);
    })
    // Reducer of clear detail item
    .addCase(dProductsClearDetailItemAC, (state) => {
      state.detailItem = undefined;
    })
    // Reducer of clear filtersForm
    .addCase(dProductsClearFiltersAC, (state) => {
      state.filters = cleanFilterObject;
    })

    // Reducer of set filters
    .addCase(dProductsSetFiltersAC, (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.pageNumber = 0;
    })

    // Reducer of set checked item
    .addCase(dProductsCheckedItemAC, (state, action) => {
      state.data = state.data.map((item) => {
        if (item.id === action.payload) {
          const newItem = item;
          newItem.checked = !item.checked;
          return newItem;
        } else {
          return item;
        }
      });
    })

    // Reducer of set checked all items
    .addCase(dProductsCheckedAllItemAC, (state, action) => {
      state.data = state.data.map((item) => {
        const newItem = item;
        newItem.checked = action.payload;
        return newItem;
      });
    })
    // Reducer of clear state
    .addCase(logoutAC, (state) => {
      state.data = initialState.data;
      state.detailItem = initialState.detailItem;
      state.filters = initialState.filters;
      state.pagination = initialState.pagination;
    })

    .addCase(resetReducers, (state, action) => {
      if (action.payload !== "products") {
        state.data = initialState.data;
        state.detailItem = initialState.detailItem;
        state.filters = initialState.filters;
        state.pagination = initialState.pagination;
      }
    });
});
